/**
 * Core domain types for the coloring engine.
 *
 * IMPORTANT: this module is pure TypeScript. It must NOT import Phaser, the DOM,
 * IndexedDB, or any browser API, so the coloring core stays unit-testable in Node.
 */

/** Stable identifiers for the 5 colorable regions of the P0A turtle. */
export type RegionId = 'head' | 'outer_shell' | 'center_shell' | 'front_flippers' | 'rear_flippers';

/** The full ordered list of region ids (also defines default z-order, back to front). */
export const REGION_IDS: readonly RegionId[] = [
  'outer_shell',
  'center_shell',
  'rear_flippers',
  'front_flippers',
  'head',
] as const;

/** Tools available in P0A. Exactly one brush + eraser (locked by spec). */
export type ToolId = 'marker' | 'eraser';

/** Brush identifiers. P0A ships a single solid marker. */
export type BrushId = 'marker';

/**
 * A point in NORMALIZED artwork space: both x and y in [0, 1] relative to the
 * artwork design box. Normalized coordinates are resolution-independent, so the
 * same operation replays identically at any viewport size or device pixel ratio.
 */
export interface NormPoint {
  x: number;
  y: number;
}

/** A point in screen/client pixels (as delivered by a PointerEvent). */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** A point in game/logical space (Phaser scale-manager space). */
export interface GamePoint {
  x: number;
  y: number;
}

/** Axis-aligned placement rectangle (in game space) of the artwork on screen. */
export interface PlacementRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type OperationType = 'paint' | 'erase';

/**
 * A single append-only drawing operation — the atomic unit of the source of
 * truth. Operations are never mutated after they are committed.
 */
export interface DrawOperation {
  /** Globally unique id for this operation. */
  id: string;
  /** Deterministic, monotonically increasing sequence number (replay order). */
  seq: number;
  type: OperationType;
  /** Region this stroke is locked to for its entire lifetime. */
  regionId: RegionId;
  brushId: BrushId;
  /** Paint color as #rrggbb. Ignored for erase operations but stored for replay stability. */
  color: string;
  /** Brush radius normalized to artwork width (fraction, e.g. 0.03 = 3% of width). */
  size: number;
  /** Ordered, interpolated stroke points in normalized artwork space. */
  points: NormPoint[];
  /** Wall-clock timestamp (ms). Not used for ordering; `seq` is authoritative. */
  timestamp: number;
}

/** The persisted artwork document (source of truth + light UI state). */
export interface ArtworkDocument {
  /** Schema version, see persistence/schema.ts. */
  version: number;
  /** Append-only committed operations, ordered by seq. */
  operations: DrawOperation[];
  /** Next sequence number to assign. */
  nextSeq: number;
  selectedColor: string;
  selectedTool: ToolId;
  /** Last update time (ms since epoch). */
  updatedAt: number;
}
